import React, { useContext, useEffect, useMemo } from "react";
import { HolderOutlined } from "@ant-design/icons";
import type { DragEndEvent } from "@dnd-kit/core";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Col, Row, Input, FormInstance, Empty } from "antd";
import { useTranslation } from "react-i18next";
import SubQuestions, { Question } from "./CustomQuestion";
import { AiFillDelete } from "react-icons/ai";
import { getUuid } from "@src/helpers/functions";
import { useDispatch } from "react-redux";
import { AppDispatch, dynamicSet, useDynamicSelector } from "@src/redux/index";
import { debounce } from "lodash";
import { IoMdAddCircle } from "react-icons/io";

export interface ParentQuestion {
  key: string;
  name: string;
  order?: number;
  id: string;
  orderNumber: number;
  customTestList?: Question[];
  removableCustomTestIdList?: string[];
}

interface props {
  form: FormInstance;
  setQuestion: any;
}

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}

const RowContext = React.createContext<RowContextProps>({});

const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move", color: "white" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

interface SortableRowProps {
  "data-row-key": string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const SortableRow: React.FC<SortableRowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  );

  return (
    <RowContext.Provider value={contextValue}>
      <div ref={setNodeRef} style={style} {...attributes}>
        {props.children}
      </div>
    </RowContext.Provider>
  );
};

const QuestionComponent: React.FC<props> = (props) => {
  const { form, setQuestion } = props;
  const { t } = useTranslation();

  // Replace useState with Redux dynamicSet and useDynamicSelector
  const dataSource = useDynamicSelector("data_source");
  const {
    removableCustomTestGroupIdList: removableCustomTestGroupIdList,
  } = useDynamicSelector("removable_custom_test_group_id_list");
  const dispatch = useDispatch<AppDispatch>();

  const handleAdd = () => {
    const newKey = (dataSource?.length + 1).toString();
    const newRow: ParentQuestion = {
      key: newKey,
      name: ``,
      id: getUuid(),
      orderNumber: Number(newKey),
      customTestList: [],
    };
    let values = dataSource?.length > 0 ? [...dataSource, newRow] : [newRow];
    dispatch(dynamicSet("data_source", values));
  };

  const handleRemove = (key: string, id: string) => {
    dispatch(
      dynamicSet("removable_custom_test_group_id_list", {
        removableCustomTestGroupIdList:
          removableCustomTestGroupIdList?.length > 0
            ? [...removableCustomTestGroupIdList, id]
            : [id],
      })
    );
    dispatch(
      dynamicSet(
        "data_source",
        dataSource?.filter((item: ParentQuestion) => item.key !== key)
      )
    );
  };

  const handleChange = (
    key: string,
    field: keyof ParentQuestion,
    value: any
  ) => {
    dispatch(
      dynamicSet(
        "data_source",
        dataSource?.map((item: ParentQuestion) =>
          item.key === key ? { ...item, [field]: value } : item
        )
      )
    );
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const newData = arrayMove(
        dataSource,
        dataSource?.findIndex(
          (record: ParentQuestion) => record.key === active.id
        ),
        dataSource?.findIndex(
          (record: ParentQuestion) => record.key === over?.id
        )
      );
      dispatch(dynamicSet("data_source", newData));
    }
  };

  useEffect(() => {
    if (dataSource?.length > 0) {
      setQuestion(dataSource);
    } else {
      setQuestion([]);
    }
  }, [dataSource, setQuestion]);

  // const process_data_source = (
  //   data_source: ParentQuestion[],
  //   sub_questions: any[]
  // ) => {
  //   return data_source.map((list: ParentQuestion) => {
  //     const find_one = sub_questions.filter(
  //       (question: any) => question?.parent_id === list?.id
  //     );
  //     return {
  //       ...list,
  //       question: find_one,
  //     };
  //   });
  // };
  // const debounced_update_data_source = useMemo(
  //   () =>
  //     debounce((updated_data_source: ParentQuestion[]) => {
  //       dispatch(dynamicSet("data_source", updated_data_source));
  //     }, 3000), // Adjust debounce time as needed
  //   []
  // );

  // useEffect(() => {
  //   if (sub_questions && sub_questions.length > 0) {
  //     const updated_data_source = process_data_source(
  //       data_source,
  //       sub_questions
  //     );
  //     debounced_update_data_source(updated_data_source);
  //   }
  // }, [sub_questions, data_source, debounced_update_data_source]);

  return (
    <div>
      <div className="level-title-container">
        <h4>{t("custom_test")}</h4>
        <Button onClick={handleAdd} icon={<IoMdAddCircle size={20} />} />
      </div>
      <div className="question-container">
        {dataSource?.length > 0 ? (
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={
                dataSource?.length > 0 &&
                dataSource?.map((item: ParentQuestion) => item.key)
              }
              strategy={verticalListSortingStrategy}
            >
              {dataSource?.map((record: ParentQuestion, index: number) => (
                <React.Fragment key={record.key}>
                  <SortableRow data-row-key={record.key}>
                    <Row
                      gutter={24}
                      justify={"space-between"}
                      align={"middle"}
                      className="custom-test-item"
                    >
                      <DragHandle />
                      <Col span={20}>
                        <div
                          className="column-container"
                          style={{ width: "60%" }}
                        >
                          <Input
                            value={record.name}
                            onChange={(e) =>
                              handleChange(record.key, "name", e.target.value)
                            }
                          />
                        </div>
                      </Col>
                      <Col span={2} style={{ textAlign: "center" }}>
                        <Button
                          type="text"
                          icon={<AiFillDelete color="white" size={18} />}
                          onClick={() => handleRemove(record.key, record.id)}
                          style={{ color: "white" }}
                        />
                      </Col>
                    </Row>
                    <div>
                      <SubQuestions
                        // set_question={(questions: ParentQuestion) =>
                        //   dispatch(dynamicSet("sub_questions", questions))
                        // }
                        form={form}
                        record={record}
                        // set_question={undefined}
                      />
                    </div>
                  </SortableRow>
                </React.Fragment>
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Empty description={t("no_custom_tests")} />
        )}
      </div>
    </div>
  );
};

export default QuestionComponent;
